import { useEffect, useId, useMemo, useRef, useState, type FormEvent } from 'react';
import { BrowserRouter, Link, NavLink, useLocation, useNavigate, useNavigationType } from 'react-router';

import { AuthProvider, useAuth } from '@/api/auth';
import {
  useDashboard,
  useAccountProfile,
  useNotificationsSummary,
  useStatus,
  useTenantUsage,
  useWatch,
} from '@/api/queries';
import { QueryProvider } from '@/api/QueryProvider';
import { EventBannerHost } from '@/features/banner/EventBanner';
import { formatCount } from '@/lib/format';

import { AppRoutes, preloadAppRoute } from './Routes';
import {
  ACCOUNT_NAV,
  ALL_NAV_ITEMS,
  AUTOMATION_NAV,
  DATA_NAV,
  PRODUCT_NAV,
  WORKSPACE_NAV,
  canUseNavItem,
  fallbackRoute,
  routeTrail,
  type ActorRole,
  type NavItem,
} from './navigation';
import './app-shell.css';

const RU_TIMEZONE_LABELS: Record<string, string> = {
  'Europe/Moscow': 'Москва',
  'Asia/Bangkok': 'Бангкок',
  'Asia/Dubai': 'Дубай',
  'Europe/Istanbul': 'Стамбул',
  'Europe/London': 'Лондон',
  'America/New_York': 'Нью-Йорк',
};

const SEARCH_COMMANDS = ALL_NAV_ITEMS.map((item) => ({
  ...item,
  search: `${item.label} ${item.to}`.toLocaleLowerCase('ru-RU'),
}));

function Navigation({ items, label, role, onNavigate }: { items: NavItem[]; label: string; role?: ActorRole; onNavigate?: () => void }) {
  return (
    <nav className="app-nav" aria-label={label}>
      {items.filter((item) => canUseNavItem(item, role)).map((item) => (
        <NavLink key={item.to} to={item.to} end={item.to === '/'} onClick={onNavigate} onFocus={() => void preloadAppRoute(item.to)} onMouseEnter={() => void preloadAppRoute(item.to)} className={({ isActive }) => isActive ? 'is-active' : undefined}>
          <span className="app-nav-icon" aria-hidden="true">{item.icon}</span>
          <span>{item.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

function Rail() {
  const status = useStatus();
  const usage = useTenantUsage();
  const location = useLocation();
  const role = usage.data?.actor_role;
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => setMobileOpen(false), [location.pathname, location.search, location.hash]);
  const runtime = status.data?.runtime;
  const state = runtime === 'running'
    ? { className: 'is-running', title: 'Данные обновляются', detail: 'система наблюдения работает' }
    : runtime === 'not_started'
      ? { className: 'is-demo', title: 'Демо-данные', detail: 'сборщики в этой среде не запущены' }
      : runtime === 'stopped'
        ? { className: 'is-stopped', title: 'Обновление остановлено', detail: 'показаны последние сохранённые данные' }
        : { className: 'is-loading', title: 'Проверяем данные', detail: 'получаем состояние API' };

  return (
    <aside className="app-rail" aria-label="Навигация Travel Cortex">
      <div className="app-rail-head">
        <Link to="/" aria-label="TRAVEL CORTEX" className="app-logo">
          <span className="app-logo-mark">TC</span>
          <span className="app-logo-copy"><strong>Travel Cortex</strong><em>Price intelligence</em></span>
        </Link>
        <button className="app-menu-button" type="button" aria-expanded={mobileOpen} aria-controls="app-mobile-navigation" aria-label={mobileOpen ? 'Закрыть меню «Ещё»' : 'Открыть меню «Ещё»'} onClick={() => setMobileOpen((open) => !open)}>
          <span className="app-menu-icon" aria-hidden="true">{mobileOpen ? '×' : '☰'}</span>
          <span className="app-menu-label">Ещё</span>
        </button>
      </div>
      <div id="app-mobile-navigation" className={`app-rail-menu${mobileOpen ? ' is-open' : ''}`}>
        <div className="app-mobile-search"><CommandSearch /></div>
        <div className="app-tenant" aria-label="Текущее рабочее пространство">
          <span aria-hidden="true">{usage.data?.workspace_kind === 'organization' ? '◈' : '◇'}</span>
          <div><small>Рабочее пространство</small><strong>{usage.data?.workspace_name ?? 'загружаем…'}</strong></div>
          <em>{usage.data ? 'одно доступно' : '…'}</em>
        </div>
        <div className="app-nav-group"><span className="app-nav-title">Продукты</span><Navigation items={PRODUCT_NAV} label="Продукты" role={role} onNavigate={() => setMobileOpen(false)} /></div>
        <div className="app-nav-group app-nav-secondary"><span className="app-nav-title">Автоматизация</span><Navigation items={AUTOMATION_NAV} label="Автоматизация" role={role} onNavigate={() => setMobileOpen(false)} /></div>
        <div className="app-nav-group app-nav-secondary"><span className="app-nav-title">Данные и доверие</span><Navigation items={DATA_NAV} label="Данные и доверие" role={role} onNavigate={() => setMobileOpen(false)} /></div>
        <div className="app-nav-group app-nav-secondary"><span className="app-nav-title">Рабочее пространство</span><Navigation items={WORKSPACE_NAV} label="Рабочее пространство" role={role} onNavigate={() => setMobileOpen(false)} /></div>
        <div className="app-nav-group app-nav-secondary"><span className="app-nav-title">Аккаунт</span><Navigation items={ACCOUNT_NAV} label="Аккаунт" role={role} onNavigate={() => setMobileOpen(false)} /></div>
        <div className={`app-data-status ${state.className}`}><strong><i /> {state.title}</strong><span>{state.detail}</span></div>
      </div>
    </aside>
  );
}

function CommandSearch({ enableShortcut = false }: { enableShortcut?: boolean }) {
  const navigate = useNavigate();
  const location = useLocation();
  const usage = useTenantUsage();
  const watch = useWatch();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsId = useId();
  const normalized = query.trim().toLocaleLowerCase('ru-RU');
  const results = useMemo(() => {
    const tenantCommands = usage.data ? [{
      to: '/data#workspace-usage',
      label: usage.data.workspace_name,
      icon: '◇',
      search: `${usage.data.workspace_name} ${usage.data.workspace_slug} рабочее пространство`.toLocaleLowerCase('ru-RU'),
    }] : [];
    const watchCommands = (watch.data?.items ?? []).map((item) => ({
      to: `/watch#watch-${item.id}`,
      label: `Наблюдение: ${item.destination}`,
      icon: '⌖',
      search: `наблюдение ${item.destination} ${item.origins.join(' ')} ${item.resorts.join(' ')}`.toLocaleLowerCase('ru-RU'),
    }));
    const role = usage.data?.actor_role;
    const commands = [
      ...SEARCH_COMMANDS.filter((item) => canUseNavItem(item, role)),
      ...tenantCommands,
      ...(role && role !== 'viewer' ? watchCommands : []),
    ];
    const matches = normalized
      ? commands.filter((item) => item.search.includes(normalized))
      : commands;
    return matches.slice(0, 8);
  }, [normalized, usage.data, watch.data?.items]);

  useEffect(() => {
    if (!enableShortcut) return undefined;
    const focusSearch = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const isEditing = target?.matches('input, textarea, select, [contenteditable="true"]');
      if ((event.metaKey || event.ctrlKey) && event.key.toLocaleLowerCase() === 'k') {
        event.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
        setOpen(true);
      } else if (event.key === '/' && !isEditing) {
        event.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    document.addEventListener('keydown', focusSearch);
    return () => document.removeEventListener('keydown', focusSearch);
  }, [enableShortcut]);

  useEffect(() => {
    setOpen(false);
    setQuery('');
  }, [location.pathname, location.search, location.hash]);

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const first = results[0];
    if (first) navigate(first.to);
  }

  return (
    <form className="app-search-wrap" role="search" onSubmit={submit} onKeyDown={(event) => {
      if (event.key === 'Escape') {
        setOpen(false);
        event.currentTarget.querySelector('input')?.blur();
      }
    }}>
      <label className="app-search"><span aria-hidden="true">⌕</span><input ref={inputRef} aria-label="Командный поиск" aria-keyshortcuts="Control+K Meta+K /" aria-controls={resultsId} aria-expanded={open} autoComplete="off" value={query} placeholder="Перейти к разделу или действию · ⌘K" onFocus={() => setOpen(true)} onChange={(event) => { setQuery(event.target.value); setOpen(true); }} /></label>
      {open ? (
        <div id={resultsId} className="app-search-results">
          {results.length > 0 ? <ul aria-label="Результаты командного поиска">{results.map((item) => <li key={item.to}><Link to={item.to}><span aria-hidden="true">{item.icon}</span><strong>{item.label}</strong><small>{item.to}</small></Link></li>)}</ul> : <p>Раздел не найден. Откройте «Монитор страниц».</p>}
        </div>
      ) : null}
    </form>
  );
}

function Topbar() {
  const { logout } = useAuth();
  const dashboard = useDashboard({ days: 1, include_pulse: false, include_sources: false });
  const profile = useAccountProfile();
  const usage = useTenantUsage();
  const notifications = useNotificationsSummary();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  useEffect(() => setUserMenuOpen(false), [location.pathname, location.search, location.hash]);
  useEffect(() => {
    if (!userMenuOpen) return;
    const close = (event: MouseEvent) => {
      if (!userMenuRef.current?.contains(event.target as Node)) setUserMenuOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', escape);
    };
  }, [userMenuOpen]);
  const locale = profile.data?.locale ?? 'ru-RU';
  const timezone = profile.data?.timezone ?? 'Europe/Moscow';
  const timezoneLabel = locale === 'ru-RU'
    ? RU_TIMEZONE_LABELS[timezone] ?? timezone
    : timezone.split('/').at(-1)?.replaceAll('_', ' ') ?? timezone;
  const date = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit', timeZone: timezone }).format(new Date());
  const observations = dashboard.data?.totals?.observations;
  const variants = dashboard.data?.totals?.variants;
  const unread = notifications.data?.unread ?? 0;
  const displayName = profile.data?.display_name ?? 'Владелец';
  const initials = displayName.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toLocaleUpperCase('ru-RU') || 'VL';
  const metric = (value: number | undefined) => value === undefined ? '—' : formatCount(value);
  return (
    <header className="app-topbar">
      <div className="app-place" title={timezone}><strong>{timezoneLabel}</strong><span>{` · ${date}`}</span></div>
      <div className="app-top-metrics" role="group" aria-label="Объём данных для анализа" aria-busy={dashboard.isPending}>
        <Link to="/data" title={observations === undefined ? 'Считаем обработанные цены' : `Обработано ${formatCount(observations)} цен. Открыть Центр данных`}>
          <small data-short="Цен">Цен обработано</small><strong>{metric(observations)}</strong>
        </Link>
        <Link to="/tours?mode=all" title={variants === undefined ? 'Считаем сопоставимые варианты' : `${formatCount(variants)} сопоставимых вариантов в каталоге`}>
          <small data-short="Вариантов">Сопоставимых</small><strong>{metric(variants)}</strong>
        </Link>
      </div>
      <CommandSearch enableShortcut />
      <div className="app-top-actions">
        <Link className="app-bell" to="/notifications?state=unread" aria-label={unread > 0 ? `Новые уведомления: ${formatCount(unread)}` : 'Новых уведомлений нет'}>♧{unread > 0 ? <strong>{unread > 99 ? '99+' : unread}</strong> : null}</Link>
        <div className="app-user-menu" ref={userMenuRef}>
          <button className="app-avatar" type="button" aria-expanded={userMenuOpen} aria-controls="app-user-menu" aria-label="Меню пользователя" onClick={() => setUserMenuOpen((open) => !open)}>{initials}</button>
          {userMenuOpen ? <div id="app-user-menu" className="app-user-popover">
            <header><span>{displayName}</span><strong>{usage.data?.workspace_name ?? 'Travel Cortex'}</strong><small>{usage.data ? `роль: ${usage.data.actor_role}` : 'читаем роль…'}</small></header>
            <Link to="/account/profile">Профиль</Link>
            <Link to="/account/security">Безопасность и сессии</Link>
            <Link to="/data#workspace-usage">Использование пространства</Link>
            {usage.data?.actor_role === 'owner' || usage.data?.actor_role === 'admin' ? <Link to="/data#audit">Журнал аудита</Link> : null}
            <button type="button" onClick={() => void logout()}>Выйти</button>
          </div> : null}
        </div>
      </div>
    </header>
  );
}

function RouteBreadcrumbs() {
  const location = useLocation();
  const navigate = useNavigate();
  const trail = routeTrail(location.pathname, location.hash);
  if (trail.length === 0) return null;
  const canGoBack = location.key !== 'default';
  return (
    <div className="app-route-context">
      <button type="button" onClick={() => canGoBack ? navigate(-1) : navigate(fallbackRoute(location.pathname))} aria-label="Вернуться на предыдущий экран">← Назад</button>
      <nav aria-label="Хлебные крошки">
        <Link to="/">Главная</Link>
        {trail.map((item, index) => <span key={`${item.label}-${index}`}><i aria-hidden="true">/</i>{item.to ? <Link to={item.to}>{item.label}</Link> : <em aria-current={index === trail.length - 1 ? 'page' : undefined}>{item.label}</em>}</span>)}
      </nav>
    </div>
  );
}

function RouteViewportManager() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const positions = useRef(new Map<string, number>());

  useEffect(() => {
    let attempt = 0;
    let timeout = 0;
    const focusMain = () => document.getElementById('app-main')?.focus({ preventScroll: true });
    const revealHash = () => {
      const rawId = location.hash.slice(1);
      let id = rawId;
      try {
        id = decodeURIComponent(rawId);
      } catch {
        id = rawId;
      }
      const target = id ? document.getElementById(id) : null;
      if (target) {
        if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
        target.scrollIntoView({ block: 'start' });
        target.focus({ preventScroll: true });
        return;
      }
      attempt += 1;
      if (attempt < 20) timeout = window.setTimeout(revealHash, 50);
      else focusMain();
    };

    if (location.hash) revealHash();
    else {
      const top = navigationType === 'POP' ? positions.current.get(location.key) ?? 0 : 0;
      window.scrollTo({ top, left: 0 });
      timeout = window.setTimeout(focusMain, 0);
    }

    return () => {
      window.clearTimeout(timeout);
      positions.current.set(location.key, window.scrollY);
    };
  }, [location.hash, location.key, navigationType]);

  return null;
}

function ProductShell() {
  return <div className="app-product-shell"><a className="app-skip-link" href="#app-main">К содержанию</a><Rail /><div className="app-workspace"><Topbar /><RouteViewportManager /><main id="app-main" className="app-main" tabIndex={-1}><RouteBreadcrumbs /><AppRoutes /></main><EventBannerHost /></div></div>;
}

function Shell() {
  const location = useLocation();
  const { status } = useAuth();
  const bare = location.pathname === '/login'
    || location.pathname === '/accept-invite'
    || location.pathname.startsWith('/shared/shortlist/')
    || status !== 'authenticated';
  return <div className="app-shell">{bare ? <main><AppRoutes /></main> : <ProductShell />}</div>;
}

export function App() {
  return <BrowserRouter basename={import.meta.env.BASE_URL}><AuthProvider><QueryProvider><Shell /></QueryProvider></AuthProvider></BrowserRouter>;
}
